

import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  Users, 
  FileText, 
  FolderOpen, 
  Paperclip, 
  TrendingUp,
  MoreHorizontal,
  Trash2,
  Crown,
  AlertCircle
} from "lucide-react";
import { api } from "@/lib/api-client";
import { formatRelativeTime } from "@/lib/utils";
import { toast } from "sonner";

interface AdminStats {
  totalUsers: number;
  totalDocuments: number;
  totalWorkspaces: number;
  totalAttachments: number;
  subscriptionStats: Array<{ plan: string; _count: number }>;
}

interface RecentUser {
  id: string;
  email: string;
  name: string | null;
  createdAt: Date;
  role: string;
}

export function AdminPanel() {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [recentUsers, setRecentUsers] = useState<RecentUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteUserId, setDeleteUserId] = useState<string | null>(null);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const data = await api.get<{ success: boolean; stats: AdminStats; recentUsers: RecentUser[] }>('/admin');

      if (data.success) {
        setStats(data.stats);
        setRecentUsers(data.recentUsers);
      }
    } catch (error) {
      toast.error("Error al cargar estadísticas");
    } finally {
      setIsLoading(false);
    }
  };

  const deleteUser = async () => {
    if (!deleteUserId) return;

    try {
      const data = await api.delete<{ success: boolean }>(`/admin?userId=${deleteUserId}`);
      if (data.success) {
        setRecentUsers(recentUsers.filter((u) => u.id !== deleteUserId));
        toast.success("Usuario eliminado");
        loadStats();
      }
    } catch (error) {
      toast.error("Error al eliminar usuario");
    } finally {
      setDeleteUserId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Panel de Administración</h1>
            <p className="text-muted-foreground">Gestiona usuarios, documentos y suscripciones</p>
          </div>
          <Button onClick={loadStats} variant="outline">
            Actualizar
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Usuarios</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.totalUsers || 0}</div>
              <p className="text-xs text-muted-foreground">Total registrados</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Documentos</CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.totalDocuments || 0}</div>
              <p className="text-xs text-muted-foreground">Total creados</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Workspaces</CardTitle>
              <FolderOpen className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.totalWorkspaces || 0}</div>
              <p className="text-xs text-muted-foreground">Total creados</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Adjuntos</CardTitle>
              <Paperclip className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.totalAttachments || 0}</div>
              <p className="text-xs text-muted-foreground">Total almacenados</p>
            </CardContent>
          </Card>
        </div>

        {/* Subscription Stats */}
        <Card>
          <CardHeader>
            <CardTitle>Suscripciones</CardTitle>
            <CardDescription>Distribución de planes</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              {stats?.subscriptionStats.map((stat) => (
                <div key={stat.plan} className="flex items-center gap-2">
                  <Badge variant={stat.plan === "pro" || stat.plan === "team" ? "default" : "secondary"}>
                    {stat.plan === "free" && "Gratis"}
                    {stat.plan === "pro" && "Pro"}
                    {stat.plan === "team" && "Team"}
                  </Badge>
                  <span className="text-2xl font-bold">{stat._count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="users">
          <TabsList>
            <TabsTrigger value="users">Usuarios</TabsTrigger>
            <TabsTrigger value="documents">Documentos</TabsTrigger>
            <TabsTrigger value="settings">Configuración</TabsTrigger>
          </TabsList>

          <TabsContent value="users" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Usuarios Recientes</CardTitle>
                <CardDescription>Últimos usuarios registrados</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Nombre</TableHead>
                      <TableHead>Rol</TableHead>
                      <TableHead>Registrado</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recentUsers.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">{user.email}</TableCell>
                        <TableCell>{user.name || "-"}</TableCell>
                        <TableCell>
                          <Badge variant={user.role === "admin" ? "default" : "secondary"}>
                            {user.role}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatRelativeTime(user.createdAt)}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteUserId(user.id)}
                            className="text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="documents">
            <Card>
              <CardHeader>
                <CardTitle>Gestión de Documentos</CardTitle>
                <CardDescription>Ver y gestionar todos los documentos</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Funcionalidad en desarrollo...
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle>Configuración del Sistema</CardTitle>
                <CardDescription>Configuración global de la aplicación</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h3 className="font-medium">Modo Mantenimiento</h3>
                    <p className="text-sm text-muted-foreground">
                      Deshabilitar temporalmente el acceso a usuarios
                    </p>
                  </div>
                  <Button variant="outline">Activar</Button>
                </div>

                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h3 className="font-medium">Backup de Base de Datos</h3>
                    <p className="text-sm text-muted-foreground">
                      Exportar una copia de seguridad
                    </p>
                  </div>
                  <Button variant="outline">Exportar</Button>
                </div>

                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h3 className="font-medium">Limpiar Datos Antiguos</h3>
                    <p className="text-sm text-muted-foreground">
                      Eliminar documentos archivados mayores a 30 días
                    </p>
                  </div>
                  <Button variant="destructive">Limpiar</Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Delete Confirmation Dialog */}
        <Dialog open={!!deleteUserId} onOpenChange={() => setDeleteUserId(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-destructive" />
                Confirmar eliminación
              </DialogTitle>
              <DialogDescription>
                ¿Estás seguro de que deseas eliminar este usuario? Esta acción no se puede deshacer
                y eliminará todos sus documentos y datos asociados.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteUserId(null)}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={deleteUser}>
                Eliminar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
